import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Share2, Copy, Link2Off } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { usePrefs } from "@/lib/context";

export function ShareTrackingLink({ tk }) {
  const { t } = usePrefs();
  const qc = useQueryClient();
  const url = tk.share_token ? `${window.location.origin}/suivi/${tk.share_token}` : null;
  const refresh = () => qc.invalidateQueries({ queryKey: ["ticket", tk.id] });
  const share = async () => {
    try {
      const { data } = await api.post(`/tickets/${tk.id}/share-link`);
      const link = `${window.location.origin}/suivi/${data.token}`;
      refresh();
      if (navigator.share) await navigator.share({ title: `${t("trackingFor")} ${tk.number}`, url: link }).catch(() => {});
      else await copyText(link);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const copyText = async (link) => {
    try { await navigator.clipboard.writeText(link); toast.success(t("linkCopied")); }
    catch { document.querySelector('[data-testid="share-tracking-url"]')?.select(); toast(t("linkReady")); }
  };
  const copy = () => copyText(url);
  const revoke = async () => { try { await api.delete(`/tickets/${tk.id}/share-link`); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="mt-3 space-y-2 border-t border-[#e6d39f] pt-3" data-testid="share-tracking">
      <Button size="sm" variant="outline" onClick={share} className="w-full rounded-sm border-[#B8860B] text-[#996515]" data-testid="share-tracking-button"><Share2 size={14} className="me-1.5" />{t("shareLink")}</Button>
      {url && (
        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5">
            <input readOnly value={url} className="h-8 min-w-0 flex-1 rounded-sm border bg-white px-2 font-data text-[11px]" data-testid="share-tracking-url" />
            <button onClick={copy} title={t("copyLink")} className="rounded-sm border bg-white p-1.5 hover:bg-slate-50" data-testid="share-tracking-copy"><Copy size={13} /></button>
            <button onClick={revoke} title={t("revokeLink")} className="rounded-sm border bg-white p-1.5 text-rose-700 hover:bg-rose-50" data-testid="share-tracking-revoke"><Link2Off size={13} /></button>
          </div>
          <p className="text-[11px] text-slate-500">{t("linkHint")}</p>
        </div>
      )}
    </div>
  );
}
